import React, { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useToasts } from "react-toast-notifications";
import Modal from "./modal";
import Generate from "./generate";
import {
  BROWSE,
  RESET,
  SetFILTERED,
  SEED_DEFAULTS,
} from "../../../../../services/redux/slices/commerce/catalog/menus";
import {
  INSOURCE as BROWSE_PROVIDERS,
  RESET as RESET_PROVIDERS,
} from "../../../../../services/redux/slices/assets/providers";
import { MDBBtn, MDBCard, MDBCardBody, MDBIcon, MDBView } from "mdbreact";
import MenuCollapse from "./collapse";
import TableRowCount from "../../../../../components/pagination/rows";
import Pagination from "../../../../../components/pagination";
import Swal from "sweetalert2";
import Search from "../../../../../components/searchables/search";
import TableLoading from "../../../../../components/tableLoading";
import ExportToExcel from "./exportToExcel";
import { axioKit } from "../../../../../services/utilities";
// import PDFExport from "../../../../../services/utilities/export/pdf/menus";

const Menus = () => {
  const [selected, setSelected] = useState({}),
    [showModal, setShowModal] = useState(false),
    [searchKey, setSearchKey] = useState(""),
    [page, setPage] = useState(1),
    [totalPages, setTotalPages] = useState(1),
    [willCreate, setWillCreate] = useState(true),
    [visible, setVisible] = useState(false),
    [showExport, setShowExport] = useState(false),
    { token, activePlatform, maxPage } = useSelector(({ auth }) => auth),
    { collections, message, isSuccess, isLoading, filtered } = useSelector(
      ({ menus }) => menus
    ),
    { addToast } = useToasts(),
    dispatch = useDispatch();

  // pages follow what is shown (the search results), not the whole list
  useEffect(() => {
    const totalPages = Math.max(Math.ceil(filtered.length / maxPage), 1);
    setTotalPages(totalPages);
    if (page > totalPages) setPage(totalPages);
  }, [filtered, page, maxPage]);

  const isSearching = filtered.length !== collections.length;

  // stable, so the debounced search is not recreated on every render;
  // results start on page 1, and no match shows none (not everything)
  const handleFiltered = useCallback(
    (results) => {
      dispatch(SetFILTERED(results));
      setPage(1);
    },
    [dispatch]
  );
  const handleResetSearch = useCallback(() => {
    dispatch(SetFILTERED(collections));
    setPage(1);
  }, [dispatch, collections]);

  // suggest the seeder menus when the branch has no menus yet
  const suggestDefaultMenus = useCallback(async () => {
    const branchId = activePlatform?.branchId;
    if (!token || !branchId) return;

    let seeds = [],
      category = "";
    try {
      const { payload } = await axioKit.universal(
        "commerce/catalog/menus/defaults",
        token,
        { branchId }
      );
      seeds = payload?.seeds || [];
      category = payload?.category || "";
    } catch (error) {
      return addToast(error.message, { appearance: "error" });
    }

    seeds = seeds.filter(({ count }) => count > 0);
    if (!seeds.length) return;

    const seedLabel = (name) => name.charAt(0).toUpperCase() + name.slice(1);

    // one option per department, plus a combined option
    const inputOptions = seeds.reduce(
      (options, { name, count }) => ({
        ...options,
        [name]: `${seedLabel(name)} only (${count} menus)`,
      }),
      {}
    );
    if (seeds.length > 1) {
      const names = seeds.map(({ name }) => name);
      const total = seeds.reduce((sum, { count }) => sum + count, 0);
      inputOptions[names.join(",")] = `${names
        .map(seedLabel)
        .join(" + ")} (${total} menus)`;
    }

    // preselect only when the branch category tells us what it offers
    const optionKeys = Object.keys(inputOptions);
    const inputValue = category
      ? optionKeys[optionKeys.length - 1]
      : seeds.length === 1
      ? optionKeys[0]
      : "";

    const { isConfirmed, value } = await Swal.fire({
      title: "No menus yet",
      icon: "info",
      html: `
        <p class="mb-0">This branch has no menus yet. Select a department to use its default menu.</p>
        <small class="d-block text-muted mt-2">Prices can be edited after.</small>
      `,
      input: "select",
      inputOptions,
      inputValue,
      inputPlaceholder: "Select department",
      inputValidator: (selected) =>
        !selected && "Please select a department.",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Use default menu",
      cancelButtonText: "Not now",
    });

    if (isConfirmed) {
      dispatch(
        SEED_DEFAULTS({ data: { branchId, seeds: value.split(",") }, token })
      );
    }
  }, [token, activePlatform?.branchId, dispatch, addToast]);

  const suggestRef = useRef(suggestDefaultMenus),
    promptedBranchRef = useRef("");
  suggestRef.current = suggestDefaultMenus;

  //Initial Browse
  useEffect(() => {
    const branchId = activePlatform?.branchId;

    if (token && branchId) {
      dispatch(BROWSE({ token, key: { branchId } })).then((action) => {
        if (
          BROWSE.fulfilled.match(action) &&
          !action.payload?.payload?.length &&
          promptedBranchRef.current !== branchId
        ) {
          promptedBranchRef.current = branchId;
          suggestRef.current();
        }
      });
    }

    return () => dispatch(RESET());
  }, [token, dispatch, activePlatform]);

  //Initial Browse
  useEffect(() => {
    if (token && activePlatform?.branchId) {
      dispatch(
        BROWSE_PROVIDERS({
          token,
          key: {
            vendors: activePlatform?.branchId,
            status: "approved",
            categories: ["ctr", "mbs", "wls"],
          },
        })
      );
    }

    return () => dispatch(RESET_PROVIDERS());
  }, [token, dispatch, activePlatform]);

  //Modal toggle
  const toggleModal = () => setShowModal(!showModal);

  const handleUpdate = (selected) => {
    setSelected(selected);
    if (willCreate) setWillCreate(false);
    setShowModal(true);
  };

  const handleCreate = () => {
    if (!willCreate) setWillCreate(true);
    setShowModal(true);
  };

  //Toast for errors or success
  useEffect(() => {
    if (message) {
      addToast(message, {
        appearance: isSuccess ? "success" : "error",
      });
    }

    return () => dispatch(RESET());
  }, [isSuccess, message, addToast, dispatch]);

  const resetSearch = () => setSearchKey("");

  // const handleGenerate = () => {
  //   setVisible(!visible);
  // };

  const handleChangePage = (isAdd) => {
    setPage((prev) => (isAdd ? prev + 1 : prev - 1));
  };

  return (
    <>
      <MDBCard narrow>
        <MDBView
          cascade
          className="gradient-card-header blue-gradient narrower py-2 mx-4 mb-3 d-flex justify-content-between align-items-center"
        >
          <span className="white-text mx-3">
            {isSearching
              ? `${filtered.length} of ${collections.length} Menus`
              : `${collections.length} Available Menus`}
          </span>
          <div className="d-flex align-items-center justify-content-between">
            <Search
              collections={collections}
              setFiltered={handleFiltered}
              reset={handleResetSearch}
              haveAction={false}
            />

            {!isLoading && !collections.length && (
              <MDBBtn
                onClick={suggestDefaultMenus}
                outline
                color="white"
                rounded
                size="sm"
                className="px-2 ml-3"
                title="Use Default Menu"
              >
                <MDBIcon icon="magic" className="mr-1" />
                Default Menu
              </MDBBtn>
            )}
            <MDBBtn
              onClick={handleCreate}
              disabled={isLoading}
              outline
              color="white"
              rounded
              size="sm"
              className="px-2 ml-3"
            >
              <MDBIcon icon="plus" className="mt-0" />
            </MDBBtn>
            <MDBBtn
              onClick={() => setShowExport(true)}
              disabled={isLoading}
              outline
              color="white"
              rounded
              size="sm"
              className="px-2"
              title="Export Menus"
            >
              <MDBIcon icon="file-export" />
            </MDBBtn>
          </div>
        </MDBView>
        <MDBCardBody className="pb-0">
          {!isLoading ? (
            <>
              {isSearching && !filtered.length && (
                <p className="text-center text-muted my-4">
                  No menu matches your search.
                </p>
              )}
              <MenuCollapse
                menus={filtered}
                page={page}
                resetSearch={resetSearch}
                searchKey={searchKey}
                handleUpdate={handleUpdate}
              />
              <div className="d-flex justify-content-between align-items-center px-4">
                <TableRowCount />
                <Pagination
                  isLoading={isLoading}
                  total={totalPages}
                  page={page}
                  setPage={handleChangePage}
                />
              </div>
            </>
          ) : (
            <TableLoading />
          )}
        </MDBCardBody>
      </MDBCard>
      <Modal
        selected={selected}
        willCreate={willCreate}
        show={showModal}
        toggle={toggleModal}
      />
      <Generate visible={visible} setVisible={setVisible} />
      <ExportToExcel
        show={showExport}
        toggle={() => setShowExport(!showExport)}
      />
    </>
  );
};

export default Menus;
